import { useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import useSalesyApiClient from "@/hooks/useSalesyApiClient";

type ListDetailParams = {
  id: string;
  query?: string | null;
  limit?: number;
  refetchInterval?: number | false;
};

export const listDataQueryKey = "get-list-data";

/**
 * This API is used to get all table data for the specified list.
 * @param listDetailParams
 */
export const useInfiniteListsDetail = (listDetailParams: ListDetailParams) => {
  const { apiClient } = useSalesyApiClient();
  const itemsTotalRef = useRef<number | null>(null);

  const fetchLists = async ({ pageParam = 0 }) => {
    const response = await apiClient.lists.getItems({
      id: listDetailParams.id,
      itemsPerPage: listDetailParams.limit || 25,
      page: pageParam,
      query: listDetailParams.query,
    });

    if (pageParam === 0 && response.info?.items_total) {
      itemsTotalRef.current = response.info.items_total;
    }

    return response;
  };

  return useInfiniteQuery({
    queryKey: [listDataQueryKey, listDetailParams],
    queryFn: fetchLists,
    enabled: !!listDetailParams,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    refetchInterval: listDetailParams.refetchInterval || false,
    getNextPageParam: (lastPage) => {
      const { info } = lastPage || {};
      if (!info) return undefined;

      const itemsTotal = info?.items_total ?? itemsTotalRef.current;
      if (!itemsTotal) return undefined;

      return info.page < Math.ceil(itemsTotal / info.items_per_page) - 1 ? info.page + 1 : undefined;
    },
  });
};
